import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Home, 
  Building2, 
  Coffee, 
  HelpCircle, 
  LayoutDashboard, 
  ChevronDown, 
  ChevronUp, 
  Users, 
  Shield,
  Droplet
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { signOut } from '../lib/auth';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const currentPath = location.pathname;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(false);

  useEffect(() => {
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsAdmin(false);
        navigate('/');
      } else {
        setUser(session?.user || null);
        checkAdminStatus(session?.user);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      checkAdminStatus(session?.user);
    } catch (error) {
      console.error('Session check error:', error);
      setUser(null);
      setIsAdmin(false);
    }
  };
  
  const checkAdminStatus = async (user: any) => {
    if (!user) return;
    
    // Check if user is CEO (hamza@avnearth.com)
    const isExec = user.email === 'hamza@avnearth.com' || user.email === 'watershedwisdomcouncils@gmail.com';
    setIsAdmin(isExec);
  };

  const handleSignOut = async () => {
    try {
      const result = await signOut();
      if (result.success) {
        setUser(null);
        setIsAdmin(false);
        navigate('/');
      }
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const mainNavItems = [
    { path: '/avn', icon: <Building2 className="h-5 w-5" />, label: 'How AVN Works' },
    { 
      id: 'partners',
      icon: <Users className="h-5 w-5" />, 
      label: 'Our Work',
      isDropdown: true,
      children: [
        { path: '/edwards-hamlet', icon: <Building2 className="h-5 w-5" />, label: "Edward's Hamlet" },
        { path: '/unity-bean', icon: <Coffee className="h-5 w-5" />, label: 'Unity Bean' },
        { path: '/not-on-our-plates', icon: <Shield className="h-5 w-5" />, label: '#NotOnOurPlates' },
        { path: '/education', icon: <Building2 className="h-5 w-5" />, label: 'REGEN Campus' },
        { path: '/water-unity', icon: <Droplet className="h-5 w-5" />, label: 'Water Unity' },
      ]
    },
    { path: '/market-insights', icon: <Building2 className="h-5 w-5" />, label: 'Market Insights' },
    { path: '/faqs', icon: <HelpCircle className="h-5 w-5" />, label: 'FAQs' }
  ];
  
  // Only add the Executive Dashboard if the user is an admin
  if (isAdmin) {
    mainNavItems.push({ 
      path: '/executive-dashboard', 
      icon: <LayoutDashboard className="h-5 w-5" />, 
      label: 'Executive Dashboard' 
    });
  }

  // Only show home link on non-home pages
  if (!isHome) {
    mainNavItems.unshift({ path: '/', icon: <Home className="h-5 w-5" />, label: 'Home' });
  }

  const userNavItems = user 
    ? [
        { 
          path: '#',
          icon: <Shield className="h-5 w-5" />,
          label: 'Sign Out',
          onClick: handleSignOut
        }
      ]
    : [
        { path: '/login', icon: <Shield className="h-5 w-5" />, label: 'Sign In' },
        { path: '/signup', icon: <Shield className="h-5 w-5" />, label: 'Join Now', highlight: true }
      ];

  const togglePartnersMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setPartnersOpen(!partnersOpen);
  };

  return (
    <nav aria-label="Main Navigation" className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isHome && !isScrolled && !isMenuOpen ? 'bg-transparent' : 'bg-avn-purple-700 shadow-lg'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" aria-label="AVN Home">
            <Building2 className="h-8 w-8 text-white" />
            <span className="font-bold text-white text-xl">AVN</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {/* Main Nav Items */}
            <div className="flex items-center gap-6">
              {mainNavItems.map((item) => 
                item.isDropdown ? (
                  <div key={item.id} className="relative">
                    <button
                      onClick={togglePartnersMenu}
                      className={`flex items-center gap-2 text-white/90 hover:text-white transition-colors`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {partnersOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    
                    {partnersOpen && (
                      <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {item.children?.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                                currentPath === child.path || 
                                (child.path !== '/' && currentPath.startsWith(child.path)) 
                                  ? 'bg-gray-100 font-medium' 
                                  : ''
                              }`}
                              aria-current={currentPath === child.path ? 'page' : undefined}
                              onClick={() => setPartnersOpen(false)}
                            >
                              {child.icon}
                              <span>{child.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 text-white/90 hover:text-white transition-colors ${
                      currentPath === item.path || 
                      (item.path !== '/' && currentPath.startsWith(item.path)) 
                        ? 'text-white font-medium border-b-2 border-white pb-1' 
                        : ''
                    }`}
                    aria-current={currentPath === item.path ? 'page' : undefined}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                )
              )}
            </div>

            {/* Divider */}
            {userNavItems.length > 0 && <div className="h-6 w-px bg-white/20"></div>}

            {/* User Nav Items */}
            {userNavItems.length > 0 && (
              <div className="flex items-center gap-6">
                {userNavItems.map((item) => (
                  item.onClick ? (
                    <button
                      key={item.path}
                      onClick={item.onClick}
                      className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2 transition-colors relative ${
                        item.highlight
                          ? `bg-white text-avn-purple-700 px-4 py-2 rounded-lg hover:bg-avn-purple-50 ${
                              item.label === 'Sign Up' ? 'mr-2' : ''
                            }`
                          : `text-white/90 hover:text-white ${
                              currentPath === item.path || 
                              (item.path !== '/' && currentPath.startsWith(item.path))
                                ? 'text-white font-medium border-b-2 border-white pb-1'
                                : ''
                            }`
                      }`}
                      aria-current={currentPath === item.path ? 'page' : undefined}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4" role="menu">
            <div className="flex flex-col gap-4">
              {mainNavItems.map((item) => 
                item.isDropdown ? (
                  <div key={item.id} className="flex flex-col">
                    <button
                      onClick={togglePartnersMenu}
                      className="flex items-center justify-between p-2 text-white/90 hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {item.icon}
                        <span>{item.label}</span>
                      </div>
                      {partnersOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    
                    {partnersOpen && (
                      <div className="ml-6 mt-2 space-y-2 border-l-2 border-white/10 pl-4">
                        {item.children?.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`flex items-center gap-2 p-2 text-white/80 hover:text-white transition-colors ${
                              currentPath === child.path || 
                              (child.path !== '/' && currentPath.startsWith(child.path))
                                ? 'text-white font-medium bg-white/10 rounded'
                                : ''
                            }`}
                            aria-current={currentPath === child.path ? 'page' : undefined}
                          >
                            {child.icon}
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-3 text-white/90 hover:text-white transition-colors p-2 ${
                      currentPath === item.path || 
                      (item.path !== '/' && currentPath.startsWith(item.path))
                        ? 'text-white font-medium bg-white/10'
                        : ''
                    }`}
                    role="menuitem"
                    aria-current={currentPath === item.path ? 'page' : undefined}
                  >
                    {item.icon} 
                    <span>{item.label}</span>
                  </Link>
                )
              )}
              {userNavItems.length > 0 && (
                <>
                  <div className="h-px bg-white/20 my-2"></div>
                  {userNavItems.map((item) => (
                    item.onClick ? (
                      <button
                        key={item.path}
                        onClick={() => {
                          item.onClick?.();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center gap-3 text-white/90 hover:text-white transition-colors p-2"
                        role="menuitem"
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 transition-colors p-2 ${
                          item.highlight
                            ? 'bg-white text-avn-purple-700 rounded-lg'
                            : 'text-white/90 hover:text-white'
                        }`}
                        role="menuitem"
                        aria-current={currentPath === item.path ? 'page' : undefined}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    )
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;